import { CURR_ENV } from '@/core/constants/appConfig/environment';
import { ScriptHandler } from '@/core/handlers/script';
import { Logger } from '@/shared/logger/logger';

import { DataLayerEventSender } from './event_sender';

/**
 * Enum defining all possible data layer types
 */
export enum EDataLayerType {
  GA4 = 'gaLayer',
  GADS = 'gadsLayer',
  GTM = 'gtmLayer',
  SGTM = 'sgtmLayer',
  WSGTM = 'wsgtmLayer',
  // TODO: Add wtag layer capturing here.
}

/**
 * Type for data layer event structure
 */
type IDataLayerEvent = {
  [key: string]: any;
  event: string;
};

/**
 * Singleton class for listening to Google data layers
 * Proactively sets up listeners for all possible data layers and handles their initialization
 */
export class DataLayerListener {
  private static instance: DataLayerListener;
  private readonly dataLayers: Map<EDataLayerType, any[]>;
  private readonly originalPushMethods: Map<
    EDataLayerType,
    (...items: any[]) => number
  >;
  private readonly initializedLayers = new Set<EDataLayerType>();
  private observer: MutationObserver | null = null;
  private debug = false;

  private constructor() {
    this.dataLayers = new Map();
    this.originalPushMethods = new Map();
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): DataLayerListener {
    if (!DataLayerListener.instance) {
      DataLayerListener.instance = new DataLayerListener();
    }
    return DataLayerListener.instance;
  }

  /**
   * Initialize the listener for a specific layer type
   * @param layerType The type of data layer to initialize
   * @param debug Enable debug logging
   */
  public initializeLayer(layerType: EDataLayerType, debug = false): void {
    if (this.initializedLayers.has(layerType)) {
      return; // Early return if already initialized - no need to log warning
    }

    // Only set debug mode if not already set
    if (!this.debug) {
      this.debug = debug;
    }

    // Setup listener for the specific layer
    this.setupDataLayerListener(layerType);

    // Setup mutation observer if not already set up
    if (!this.observer) {
      this.setupMutationObserver();
    }

    this.initializedLayers.add(layerType);

    Logger.debug(`DataLayerListener initialized for layer ${layerType}`, {
      environment: CURR_ENV,
      layerType,
    });
  }

  /**
   * Check if a specific layer is initialized
   * @param layerType The type of data layer to check
   */
  public isLayerInitialized(layerType: EDataLayerType): boolean {
    return this.initializedLayers.has(layerType);
  }

  /**
   * Get the full data layer name for the current environment
   * @param layerType Type of data layer
   */
  private getDataLayerName(layerType: EDataLayerType): string {
    return `wtagl_${CURR_ENV}_${layerType}`;
  }

  /**
   * Setup a listener for a specific data layer type
   * @param layerType Type of data layer
   */
  private setupDataLayerListener(layerType: EDataLayerType): void {
    const dataLayerName = this.getDataLayerName(layerType);

    // Define the push method override
    const pushOverride = (...args: any[]): number => {
      // Process the pushed data
      void this.handleDataLayerPush(layerType, args);

      // Call original push method if it exists
      const originalPush = this.originalPushMethods.get(layerType);
      if (originalPush) {
        return originalPush.apply(this.dataLayers.get(layerType), args);
      }

      // If no original push method exists yet, just store the args
      return args.length;
    };

    // If data layer already exists, set it up immediately
    if (this.doesDataLayerExist(dataLayerName)) {
      this.setupExistingDataLayer(layerType, dataLayerName, pushOverride);
    } else {
      // Set up a property descriptor to catch when the data layer is created
      this.setupDataLayerPropertyDescriptor(
        layerType,
        dataLayerName,
        pushOverride,
      );
    }
  }

  /**
   * Setup an existing data layer
   * @param layerType Type of data layer
   * @param dataLayerName Full name of the data layer
   * @param pushOverride Override function for the push method
   */
  private setupExistingDataLayer(
    layerType: EDataLayerType,
    dataLayerName: string,
    pushOverride: (...items: any[]) => number,
  ): void {
    const dataLayer = (window as any)[dataLayerName];
    this.originalPushMethods.set(layerType, dataLayer.push);
    this.dataLayers.set(layerType, dataLayer);
    dataLayer.push = pushOverride;

    Logger.debug(
      `DataLayerListener: Setup complete for existing ${layerType}`,
      {
        dataLayerName,
      },
    );
  }

  /**
   * Setup a property descriptor to catch data layer creation
   * @param layerType Type of data layer
   * @param dataLayerName Full name of the data layer
   * @param pushOverride Override function for the push method
   */
  private setupDataLayerPropertyDescriptor(
    layerType: EDataLayerType,
    dataLayerName: string,
    pushOverride: (...items: any[]) => number,
  ): void {
    Object.defineProperty(window, dataLayerName, {
      configurable: true,
      // eslint-disable-next-line getter-return
      get: () => {},
      set: newValue => {
        if (Array.isArray(newValue)) {
          Object.defineProperty(window, dataLayerName, {
            configurable: true,
            enumerable: true,
            value: newValue,
            writable: true,
          });
          this.setupExistingDataLayer(layerType, dataLayerName, pushOverride);
        }
      },
    });
    Logger.debug(
      `DataLayerListener: Setup property descriptor for ${layerType}`,
      {
        dataLayerName,
      },
    );
  }

  /**
   * Setup mutation observer to detect new data layers
   */
  private setupMutationObserver(): void {
    this.observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // Only check for initialized layers
          this.initializedLayers.forEach(layerType => {
            const dataLayerName = this.getDataLayerName(layerType);
            if (
              this.doesDataLayerExist(dataLayerName) &&
              !this.dataLayers.has(layerType)
            ) {
              this.setupDataLayerListener(layerType);
            }
          });
        }
      });
    });

    this.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Check if a data layer exists in the window object
   * @param dataLayerName Full name of the data layer
   */
  private doesDataLayerExist(dataLayerName: string): boolean {
    return Array.isArray((window as any)[dataLayerName]);
  }

  /**
   * Handle data pushed to a data layer
   * @param layerType Type of data layer
   * @param args Arguments pushed to the data layer
   */
  private async handleDataLayerPush(
    layerType: EDataLayerType,
    args: any[],
  ): Promise<void> {
    // Process each argument
    for (const arg of args) {
      if (typeof arg === 'object' && arg !== null) {
        const event: IDataLayerEvent = {
          event: arg.event || 'unknown',
          source_uid: ScriptHandler.getInstance().getSourceUid(),
          ...arg,
        };

        Logger.debug(`DataLayerListener: Event received from ${layerType}`, {
          event,
          layerType,
        });
        // Send to EWS API endpoint using the static method
        try {
          await DataLayerEventSender.sendEvent(layerType, event);
        } catch (error) {
          Logger.error('DataLayerListener: Failed to send event to EWS API', {
            error,
            event,
            layerType,
          });
        }
      }
    }
  }

  /**
   * Get all monitored data layer types
   */
  public getMonitoredDataLayers(): EDataLayerType[] {
    return Array.from(this.initializedLayers);
  }

  /**
   * Reset the listener (useful for testing)
   */
  public reset(): void {
    // Restore original push methods
    this.dataLayers.forEach((dataLayer, layerType) => {
      const originalPush = this.originalPushMethods.get(layerType);
      if (originalPush) {
        dataLayer.push = originalPush;
      }
    });

    // Disconnect mutation observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.dataLayers.clear();
    this.originalPushMethods.clear();
    this.initializedLayers.clear();
    this.debug = false;
  }
}
